import './main.scss';
import { glsClearLocalStorageWhenAppVersionChange, glsCustomNavigationClient } from '@gls-its-frontends/common/gls-app-config';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { VersionService } from '@onboarding/shared/app-version/data-access';
import {
    type Environment,
    getEnvironment,
    LOCAL_STORAGE_PREFIX,
    setUpDynatrace,
    setUpEnvironmentConfig
} from '@onboarding/shared/environment/util';
import { FeatureFlagService } from '@onboarding/shared/feature-flags/util';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, type Pinia } from 'pinia';
import { createPersistedState } from 'pinia-plugin-persistedstate';
import { createApp, type Plugin } from 'vue';
import en from './i18n/en.json';

(async (): Promise<void> => {
    const environment: Environment = getEnvironment();

    await FeatureFlagService.init();
    await VersionService.init();
    glsClearLocalStorageWhenAppVersionChange(VersionService.appVersion, LOCAL_STORAGE_PREFIX);
    setUpEnvironmentConfig(environment);

    setUpDynatrace(environment);

    /**
     * `window.env` is only filled by `setUpEnvironmentConfig`, so everything reading it at module
     * scope (MSAL, the HTTP clients, the views) must be imported after that call.
     */
    const { MSAL_B2C } = await import('@onboarding/shared/authentication/data-access');
    const { default: App } = await import('./App.vue');
    const { default: router } = await import('./routing/router');

    MSAL_B2C.setNavigationClient(glsCustomNavigationClient(router));

    const app = createApp(App);

    const pinia: Pinia = createPinia().use(
        createPersistedState({
            key: (key: string): string => LOCAL_STORAGE_PREFIX + key
        })
    );
    const i18n: Plugin = glsCreateI18nInstance({ en });

    app.use(pinia).use(i18n).use(VueQueryPlugin, glsVueQueryPluginOptions).use(router).mount('#app');
})();
